/**
 * Scroll dots.
 * ------------------------------------------------------------------------------
 * Custom gallery dots for sticky position carousels.
 * used on the product-gallery section and article template.
 *
 * @namespace scrollDots
 */
import {debounce, on} from '../helpers/utils';
import cssClasses from '../helpers/cssClasses';

 /**
 * Create new dots object.
 * @param {Object} config - Dots object settings.
 * @param {String} config.container - The gallery container element.
 * @param {String} config.dotContainer - The dots container element.
 * @param {String} config.dots - Individual dot element.
 */
export default (config) => {

  /**
   * Initialise component.
   */
  function init() {
    if (!config.dotContainer) {
      return;
    }

    on('scroll', debounce(handleScrollDots), 150);
    handleClickDots();
  }

  /**
   * Handle desktop gallery dots on scroll
   */
  function handleScrollDots() {
    const galleryScroll = config.container.getBoundingClientRect();
    const lastSlide = [...config.container.querySelectorAll('.swiper-slide')].pop();

    if ((galleryScroll.bottom - lastSlide.offsetHeight) < (window.innerHeight || document.documentElement.clientHeight)) {
      config.dotContainer.classList.add('no-arrow');
    } else {
      config.dotContainer.classList.remove('no-arrow');
    }

    if (galleryScroll.bottom < (window.innerHeight || document.documentElement.clientHeight)) {
      config.dotContainer.classList.remove(cssClasses.active);
      return;
    }

    config.dotContainer.classList.add(cssClasses.active);

    config.dots.forEach((dot) => {
      const dotId = dot.dataset.image;
      const image = config.container.querySelector(`[js-gallery-dot="Image${dotId}"]`);
      const imageBounding = image.getBoundingClientRect();
      const topScrollPos = (image.parentNode.offsetTop + image.offsetTop) - (imageBounding.height / 2);
      const bottomScrollPos = (image.parentNode.offsetTop + image.offsetTop) + (imageBounding.height / 2);
      const halfHeight = imageBounding.height / 2;
      const length = config.dots.length;

      if ((window.scrollY >= topScrollPos) && (window.scrollY < bottomScrollPos)) {
        dot.classList.add(cssClasses.active);
        return;
      }

      if (((imageBounding.height * length) - halfHeight) <= window.scrollY) {
        config.dotContainer.classList.remove(cssClasses.active);
      }

      dot.classList.remove(cssClasses.active);
    });
  }

  /**
   * Handle desktop gallery dots on scroll
   */
  function handleClickDots() {
    config.dots.forEach((dot) => {
      on('click', dot, () => {
        scrollToImage(dot);

        window.dataLayer.push({
          event: 'GAevent',
          eventID: '16',
          eventCategory: 'product',
          eventAction: 'image_click',
          eventLabel: config.dots.indexOf(dot) + 1,
        });
      });
    });
  }

  /**
   * Scrolls to the image with same js-gallery-dot as selected dot.
   * @param {HTMLElement} dot - Active dot
   */
  function scrollToImage(dot) {
    const dotId = dot.dataset.image;
    const image = config.container.querySelector(`[js-gallery-dot="Image${dotId}"]`);
    image.scrollIntoView({behavior: 'smooth', block: 'start'});
  }

  /**
   * Expose public interface.
   */
  return Object.freeze({
    init,
    scrollToImage,
  });
};
